import React, { Component } from 'react'
import { APP_CONSTANTS } from 'src/consumer/constants'
import logo_brand from 'src/assets/logo_brand.png'

/**
 * PublicLiveSessionPageErrorBoundary
 * 
 * Provides graceful error handling for the Public Live Session Page.
 * If the page fails to load (network error, chunk loading failure, etc.),
 * this boundary catches the error and displays a user-friendly message
 * with retry options.
 * 
 * This is especially important because:
 * 1. The page loads heavy Agora SDK chunks that can fail on slow networks
 * 2. Users on this page are trying to join important video sessions
 * 3. We need to provide clear guidance on how to proceed
 */
export default class PublicLiveSessionPageErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0,
    }
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render shows the fallback UI
    return { hasError: true, error }
  }

  componentDidCatch(error, errorInfo) {
    // Log error for debugging (but don't expose sensitive info)
    console.error('[PublicLiveSessionPage] Error caught by boundary:', {
      message: error?.message,
      name: error?.name,
      componentStack: errorInfo?.componentStack?.slice(0, 500), // Truncate for safety
    })
    
    this.setState({ errorInfo })
    
    // Optionally report to error tracking service
    // analytics.track('session_page_error', { error: error?.message })
  }

  handleRetry = () => {
    this.setState((prev) => ({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: prev.retryCount + 1,
    }))
  }

  handleHardRefresh = () => {
    // Clear any cached chunks that might be corrupted
    if ('caches' in window) {
      caches.keys().then((names) => {
        names.forEach((name) => {
          caches.delete(name)
        })
      })
    }
    // Force hard refresh
    window.location.reload(true)
  }

  render() {
    if (this.state.hasError) {
      const isChunkLoadError = 
        this.state.error?.message?.includes('Loading chunk') ||
        this.state.error?.message?.includes('ChunkLoadError') ||
        this.state.error?.message?.includes('Failed to fetch')

      return (
        <div style={styles.container}>
          <style>{keyframes}</style>
          
          <div style={styles.card}>
            {/* Logo */}
            <img
              src={APP_CONSTANTS.APP_LOGO_LINK}
              alt="AUMHUM"
              style={styles.logo}
              onError={(e) => {
                e.target.src = logo_brand
              }}
            />

            {/* Error Icon */}
            <div style={styles.iconContainer}>
              <svg style={styles.icon} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                <path d="M12 8v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                <circle cx="12" cy="16" r="1" fill="currentColor" />
              </svg>
            </div>

            {/* Error Message */}
            <h2 style={styles.title}>
              {isChunkLoadError 
                ? 'Connection Issue' 
                : 'Something went wrong'}
            </h2>
            
            <p style={styles.message}>
              {isChunkLoadError 
                ? 'We had trouble loading the video session. This usually happens due to a slow or unstable internet connection.'
                : 'We encountered an unexpected issue while loading your session. Don\'t worry - this can usually be fixed with a quick refresh.'}
            </p>

            {/* Retry Button */}
            <button 
              onClick={this.handleRetry}
              style={styles.primaryButton}
            >
              Try Again
            </button>

            {/* Hard Refresh Option (after first retry) */}
            {this.state.retryCount > 0 && (
              <>
                <p style={styles.hint}>
                  Still not working? Try a full refresh:
                </p>
                <button 
                  onClick={this.handleHardRefresh}
                  style={styles.secondaryButton}
                >
                  Full Page Refresh
                </button>
              </>
            )}

            {/* Support Link */}
            <a 
              href={APP_CONSTANTS.THERAPY_SUPPORT_WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              style={styles.supportLink}
            >
              Contact Support
            </a>

            {/* Network Tips (for chunk load errors) */}
            {isChunkLoadError && (
              <div style={styles.tips}>
                <p style={styles.tipsTitle}>Quick tips:</p>
                <ul style={styles.tipsList}>
                  <li>Check your internet connection</li>
                  <li>Move closer to your WiFi router</li>
                  <li>Try switching from WiFi to mobile data</li>
                  <li>Close other apps using the internet</li>
                </ul>
              </div>
            )}
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

// Styles - inline to avoid any additional imports
const styles = {
  container: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #fafbfc 0%, #f5f7fa 50%, #f0f2f5 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  card: {
    background: '#ffffff',
    borderRadius: '24px',
    padding: '40px 32px',
    maxWidth: '440px',
    width: '100%',
    textAlign: 'center',
    boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08), 0 1px 4px rgba(0, 0, 0, 0.04)',
    border: '1px solid #e5e7eb',
  },
  logo: {
    height: '56px',
    width: '56px',
    borderRadius: '12px',
    marginBottom: '24px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
  },
  iconContainer: {
    width: '64px',
    height: '64px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(239, 68, 68, 0.05) 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 20px',
    animation: 'pulse 2s ease-in-out infinite',
  },
  icon: {
    width: '32px',
    height: '32px',
    color: '#ef4444',
  },
  title: {
    fontSize: '22px',
    fontWeight: 700,
    color: '#111827',
    margin: '0 0 12px',
  },
  message: {
    fontSize: '15px',
    color: '#6b7280',
    lineHeight: 1.6,
    margin: '0 0 24px',
  },
  primaryButton: {
    width: '100%',
    padding: '14px 24px',
    fontSize: '16px',
    fontWeight: 600,
    color: '#ffffff',
    background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
    border: 'none',
    borderRadius: '12px',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(139, 92, 246, 0.3)',
    transition: 'all 0.2s ease',
    marginBottom: '12px',
  },
  secondaryButton: {
    width: '100%',
    padding: '12px 24px',
    fontSize: '15px',
    fontWeight: 500,
    color: '#4b5563',
    background: '#ffffff',
    border: '1px solid #d1d5db',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    marginBottom: '16px',
  },
  hint: {
    fontSize: '13px',
    color: '#9ca3af',
    margin: '8px 0 12px',
  },
  supportLink: {
    display: 'inline-block',
    fontSize: '14px',
    color: '#8b5cf6',
    textDecoration: 'none',
    fontWeight: 500,
    marginTop: '8px',
  },
  tips: {
    marginTop: '24px',
    padding: '16px',
    background: '#f9fafb',
    borderRadius: '12px',
    textAlign: 'left',
  },
  tipsTitle: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#374151',
    margin: '0 0 8px',
  },
  tipsList: {
    fontSize: '13px',
    color: '#6b7280',
    margin: 0,
    paddingLeft: '20px',
    lineHeight: 1.8,
  },
}

// Keyframes
const keyframes = `
  @keyframes pulse {
    0%, 100% { transform: scale(1); opacity: 1; }
    50% { transform: scale(1.05); opacity: 0.8; }
  }
`

